/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';

// UI lib components
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Text,
  Box,
  Flex,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';

// Types
import { UserOnboardingModalProps, OnboardingFormData } from './types';

// Styles
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import { palette } from '@app/shared/ui-kit/colors';
import { useUpdateUserMutation } from '@app/types/blackroom-api';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function UserOnboardingModal({
  userId,
  workspace,
  onComplete,
}: UserOnboardingModalProps) {
  /* ********************************** HOOKS ********************************* */

  // Localization
  const { t } = useTranslation();

  // API
  const [updateUser, { loading: isSubmitting }] = useUpdateUserMutation();

  // Form
  const form = useForm<OnboardingFormData>({
    initialValues: {
      firstName: '',
      lastName: '',
    },
    validate: {
      firstName: (value) =>
        value.trim().length === 0
          ? t('User:UserOnboardingModal.form.validation.firstName')
          : null,
      lastName: (value) =>
        value.trim().length === 0
          ? t('User:UserOnboardingModal.form.validation.lastName')
          : null,
    },
  });

  /* ******************************** CALLBACKS ******************************* */

  const handleSubmit = async (values: OnboardingFormData) => {
    try {
      console.log(workspace);
      await updateUser({
        variables: {
          updateUserInput: {
            id: userId,
            workspaceId: workspace.id,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
          },
        },
      });

      showNotification({
        title: t('User:UserOnboardingModal.notifications.success.title'),
        message: t('User:UserOnboardingModal.notifications.success.message'),
        color: 'green',
      });

      onComplete();
    } catch (error) {
      console.error('Error updating user profile:', error);
      showNotification({
        title: t('User:UserOnboardingModal.notifications.error.title'),
        message: t('User:UserOnboardingModal.notifications.error.message'),
        color: 'red',
      });
    }
  };

  /* ******************************** RENDERING ******************************* */

  return (
    <Modal
      opened={true}
      onClose={() => {}} // Can't close - user must complete
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      size="md"
      trapFocus
    >
      <Box>
        {/* Header bar */}
        <Box bg="blackroomBlue" h="xs" w="100%" mb="lg" />

        {/* Content */}
        <Stack gap="md" px="md">
          <Text
            size="xl"
            fw={FONT_WEIGHT.bold}
            c={palette.blackroomBlue}
            ta="center"
          >
            {t('User:UserOnboardingModal.title')}
          </Text>

          <Text size="sm" c={palette.blackroomDarkGray3} ta="center">
            {t('User:UserOnboardingModal.description')}
          </Text>

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label={t(
                  'User:UserOnboardingModal.form.fields.firstName.label',
                )}
                placeholder={t(
                  'User:UserOnboardingModal.form.fields.firstName.placeholder',
                )}
                required
                {...form.getInputProps('firstName')}
                data-autofocus
              />

              <TextInput
                label={t('User:UserOnboardingModal.form.fields.lastName.label')}
                placeholder={t(
                  'User:UserOnboardingModal.form.fields.lastName.placeholder',
                )}
                required
                {...form.getInputProps('lastName')}
              />

              <Flex justify="center" mt="md">
                <Button
                  type="submit"
                  loading={isSubmitting}
                  color="blackroomBlue"
                  size="md"
                >
                  {t('User:UserOnboardingModal.form.submit')}
                </Button>
              </Flex>
            </Stack>
          </form>
        </Stack>
      </Box>
    </Modal>
  );
}

export default UserOnboardingModal;
